import { useState } from "react";
import { PLAYER_COLORS } from "@/lib/api";
import CellTokens, { wallLift } from "@/components/game/CellTokens";
import { wallSidesOf } from "@/components/game/EdgeWalls";
import { LAYER, WATER, heightLabel } from "@/lib/terrain";

// Layer over every block so the corner markers sit above every building. 3D BOARD: each square's
// markers sit on the part of its top face no later block covers (geom.vis), so they move with the
// lifted top face and stay readable next to taller neighbours.
export default function BoardMarkers({ geom, gridRows, gridCols, cells, players, cellInfo, illegalCell, walls = {} }) {
  const seat = Object.fromEntries(players.map((p, i) => [p.color, { n: i + 1, name: p.name }]));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: LAYER.markers }} aria-hidden="false">
      {Array.from({ length: gridRows }).flatMap((_, r) =>
        Array.from({ length: gridCols }).map((__, c) => {
          const { key, cell, legal, dimmed, height, flooded } = cellInfo(r, c);
          const v = geom.vis(key);
          const built = cell?.kind === "tile" || cell?.kind === "cube";
          const floors = cell?.kind === "cube" ? (cell.cube_stack || []).length : 0;
          // TERRAIN 2 of 3: walls built on this square sit along its edges; the owner chip, floor
          // count, height label and Green Street badge move clear of them.
          const sides = wallSidesOf(walls, key);
          const lift = wallLift(sides, "bottom");
          return (
            <div key={key} data-testid={`markers-${key}`} className="absolute" style={{ left: v.left, top: v.top, width: v.width, height: v.height, opacity: dimmed ? 0.6 : 1 }}>
              {legal && <PlaceHere plot={cell?.kind === "cube"} onArt={!!cell?.kind} />}
              {illegalCell === key && (
                <span data-testid={`illegal-tint-${key}`} className="absolute inset-0" style={{ boxShadow: "inset 0 0 0 1.5px var(--danger)" }} />
              )}
              {built && cell.kind !== "cube" && <CellTokens tokens={cell.edge_tokens} sides={sides} />}
              {built && cell.owner && <OwnerChip cellKey={key} r={r} c={c} cell={cell} seat={seat[cell.owner]} lift={lift} left={wallLift(sides, "left")} />}
              {built && !cell.owner && <BankChip cellKey={key} lift={lift} left={wallLift(sides, "left")} />}
              {floors >= 2 && (
                <span
                  data-testid={`floor-count-${key}`}
                  className="absolute font-coord"
                  style={{ right: 4 + wallLift(sides, "right"), bottom: 4 + lift, fontSize: 10.5, lineHeight: "13px", fontWeight: 700, color: "#2A2E2C", background: "rgba(251,250,247,0.9)", borderRadius: 4, padding: "0 3px" }}
                >
                  x{floors}
                </span>
              )}
              {height != null && <HeightLabel cellKey={key} height={height} onArt={!!cell?.kind && !flooded} flooded={flooded} top={3 + wallLift(sides, "top")} left={4 + wallLift(sides, "left")} />}
            </div>
          );
        }),
      )}
    </div>
  );
}

// TERRAIN 1 of 3 / 3D BOARD: the square's terrain height, top-left of the top face, on every
// square. No background; a soft pale halo keeps it readable over tile art and buildings.
function HeightLabel({ cellKey, height, onArt, flooded, top = 3, left = 4 }) {
  const color = flooded ? WATER.label : onArt ? "rgba(42,46,44,0.5)" : "rgba(42,46,44,0.38)";
  return (
    <span
      data-testid={`height-label-${cellKey}`}
      title={`Terrain height ${height}`}
      className="absolute font-coord"
      style={{
        top,
        left,
        fontSize: 9.5,
        lineHeight: "12px",
        fontWeight: onArt ? 600 : 500,
        letterSpacing: 0,
        color,
        textShadow: flooded ? undefined : onArt ? "0 0 1px #FBFAF7, 0 0 2px #FBFAF7, 0 0 3px #FBFAF7, 0 0 5px rgba(251,250,247,0.9)" : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {heightLabel(height)}
    </span>
  );
}

function PlaceHere({ plot, onArt }) {
  const box = plot
    ? { left: "19%", top: "19%", width: "62%", height: "62%", background: "rgba(36,73,90,.08)" }
    : { inset: 4, background: onArt ? "rgba(36,73,90,.08)" : undefined };
  return (
    <span data-testid="place-here" className="absolute flex items-center justify-center rounded-md" style={{ ...box, border: "1.5px dashed var(--sea)" }}>
      <span
        style={{
          fontSize: plot ? 11 : 12,
          fontWeight: 600,
          color: "var(--sea)",
          background: onArt ? "rgba(251,250,247,.9)" : undefined,
          padding: onArt ? "1px 5px" : undefined,
          borderRadius: 4,
          whiteSpace: "nowrap",
        }}
      >
        Place here
      </span>
    </span>
  );
}

// v3 step 5: a piece the Climate Bank planted (an unowned Mangrove). No owner colour: a small
// neutral round chip, so it reads as "belongs to nobody" next to the players' square chips.
function BankChip({ cellKey, lift = 0, left = 0 }) {
  return (
    <span className="absolute" style={{ left: 5 + left, bottom: 5 + lift, pointerEvents: "auto" }}>
      <span
        role="img"
        aria-label="Climate Bank Mangrove: belongs to nobody"
        title="Climate Bank Mangrove: belongs to nobody"
        data-testid={`bank-piece-${cellKey}`}
        className="block"
        style={{ width: 12, height: 12, borderRadius: 6, background: "#EDEAE0", boxShadow: "0 0 0 1.5px #FFFFFF, inset 0 0 0 2px #7C8A82" }}
      />
    </span>
  );
}

// 12 x 12 square in the owner's colour, bottom-left of the top face, white 1.5 px ring.
function OwnerChip({ cellKey, r, c, cell, seat, lift = 0, left = 0 }) {
  const [open, setOpen] = useState(false);
  const label = `Owned by Player ${seat?.n ?? "?"}${seat?.name ? ` (${seat.name})` : ""}`;
  const testid = cell.kind === "cube" ? `owner-ring-${r}-${c}` : `tile-${r}-${c}-${cell.card_key || "tile"}`;
  return (
    <span className="absolute" style={{ left: 5 + left, bottom: 5 + lift, pointerEvents: "auto" }}>
      <span
        role="img"
        aria-label={label}
        title={seat?.name || label}
        data-testid={testid}
        data-owner={cell.owner}
        onClick={() => {
          setOpen(true);
          setTimeout(() => setOpen(false), 1600);
        }}
        className="block"
        style={{ width: 12, height: 12, borderRadius: 2.5, background: PLAYER_COLORS[cell.owner], boxShadow: "0 0 0 1.5px #FFFFFF" }}
      />
      {open && (
        <span data-testid={`owner-tip-${cellKey}`} className="absolute left-0 bottom-5 whitespace-nowrap rounded-md px-2 py-1" style={{ background: "var(--ink)", color: "var(--card)", fontSize: 12, fontWeight: 600 }}>
          {seat?.name}
        </span>
      )}
    </span>
  );
}
